<template>
    <div class="mc-liveview__map" :class="{ 'detail-panel-open': selectedDetail }">
        <div class="mc-map-wrapper">
            <!-- Legend area -->
            <div class="mc-map-legend">
                <div class="mc-map-legend__item">
                    <span class="legend-marker west-marker"></span>
                    <span>West (Jakarta)</span>
                </div>
                <div class="mc-map-legend__item">
                    <span class="legend-marker east-marker"></span>
                    <span>East (Surabaya)</span>
                </div>
            </div>

            <l-map ref="UserMap" :center="mapCenter" :zoom="mapZoom" class="mc-map" style="height: 100%;"
                @ready="onMapReady" @dblclick="onMapDblClick">
                <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <v-marker-cluster v-if="clusterEnabled">
                    <l-marker v-for="tech in validUsersFiltered"
                        :key="`marker-${tech.user_id}-${tech.address?.latitude}-${tech.address?.longitude}`"
                        :lat-lng="getLatLng(tech)" :icon="getCustomIcon(tech)" :ref="`marker-${tech.user_id}`"
                        @click="onMarkerClick(tech)" />
                </v-marker-cluster>
                <template v-if="!clusterEnabled">
                    <l-marker v-for="tech in validUsersFiltered" :key="tech.user_id" :lat-lng="getLatLng(tech)"
                        :icon="getCustomIcon(tech)" :ref="'marker-' + tech.user_id" @click="onMarkerClick(tech)" />
                </template>

                <!-- DRAGGABLE MARKER untuk edit -->
                <l-marker v-if="showDraggableMarker"
                    :key="`draggable-${draggablePosition.lat}-${draggablePosition.lng}`" ref="draggableMarker"
                    :lat-lng="draggablePosition" :draggable="true" :icon="getHomePinIcon(selectedDetail)"
                    @dragend="onMarkerDragEnd" />

                <!-- Perbaikan: menggunakan properti _circleRadius yang sudah divalidasi -->
                <l-circle v-for="tech in validUsersWithRadius" :key="`circle-${tech.user_id}`"
                    :lat-lng="[tech.address?.latitude, tech.address?.longitude]" :radius="tech.address?.radius * 1000"
                    :color="tech.address?.area === 'West' ? '#3b82f6' : '#16a34a'"
                    :fill-color="tech.address?.area === 'West' ? '#3b82f6' : '#16a34a'" :fill-opacity="0.1"
                    :opacity="0.4" :weight="1" />
            </l-map>

            <transition name="slide-panel">
                <TechnicianEditPanel v-if="selectedDetail" :detail="selectedDetail" @close="closePanel" />
            </transition>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LCircle } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import TechnicianEditPanel from './TechnicianEditPanel';

export default {
    components: { LMap, LTileLayer, LMarker, LCircle, 'v-marker-cluster': Vue2LeafletMarkerCluster, TechnicianEditPanel },
    data() {
        return {
            mapObject: null,
            selectedDetail: null,
            internalUpdate: false,
            lastClickedMarker: null,
            isCentering: false,
            moveEndHandler: null,
            activeHighlightId: null,
            highlightInterval: null,
            draggablePosition: null,
            isFlying: false,
            suppressZoomSync: false,
        };
    },
    computed: {
        sidebarOpen() { return this.$store.state.sidebar.sideBarOpen; },
        clusterEnabled() { return this.$store.state.users.userCluster; },
        technicians() { return this.$store.state.users.technicians || []; },
        mapCenter() {
            // Saat sedang centering/flying, jangan biarkan Vuex store menimpa posisi map
            if ((this.internalUpdate || this.suppressZoomSync || this.isCentering || this.isFlying) && this.mapObject) {
                const center = this.mapObject.getCenter();
                return L.latLng(center.lat, center.lng);
            }
            const c = this.$store.state.map.center;
            return L.latLng(c.lat || -2.5489, c.lng || 118.0149);
        },
        mapZoom() {
            // Saat sedang centering/flying, jangan biarkan Vuex store menimpa zoom map
            if ((this.internalUpdate || this.suppressZoomSync || this.isCentering || this.isFlying) && this.mapObject) {
                return this.mapObject.getZoom();
            }
            return this.$store.state.map.zoom || 5;
        },
        selectedTechnicianFromStore() {
            return this.$store.state.map.selectedTechnician;
        },
        showDraggableMarker() {
            return !!this.selectedDetail && this.draggablePosition !== null;
        },
        areaFilter() {
            return this.$store.state.map.areaFilter || 'all';
        },
        validUsersWithRadius() {
            // Ambil semua teknisi yang punya radius valid
            let users = this.validUsers.filter(tech => {
                if (!tech.address || typeof tech.address !== 'object') return false;
                const radius = Number(tech.address.radius ?? tech.radius);
                if (isNaN(radius) || radius <= 0) return false;
                const lat = parseFloat(tech.address.latitude);
                const lng = parseFloat(tech.address.longitude);
                return !isNaN(lat) && !isNaN(lng);
            });

            // Jika ada selectedDetail, hanya tampilkan geofence miliknya
            if (this.selectedDetail) {
                users = users.filter(t => t.user_id === this.selectedDetail.user_id);
            } else {
                // Tidak ada selectedDetail → filter area
                if (this.areaFilter !== 'all') {
                    users = users.filter(t => {
                        const area = t.address?.area || t.area || 'West';
                        return area.toLowerCase() === this.areaFilter.toLowerCase();
                    });
                }
            }
            return users;
        },
        validUsersFiltered() {
            // Jika ada teknisi dipilih, sembunyikan semua marker reguler
            if (this.selectedDetail) {
                return [];
            }

            let users = this.validUsers;
            if (this.areaFilter !== 'all') {
                users = users.filter(t => {
                    const area = t.address?.area || t.area || 'West';
                    return area.toLowerCase() === this.areaFilter.toLowerCase();
                });
            }
            return users;
        },
        validUsers() {
            return this.technicians.filter(t => {
                let lat, lng;

                // 1. Utamakan address
                if (t.address && typeof t.address === 'object') {
                    lat = parseFloat(t.address.latitude);
                    lng = parseFloat(t.address.longitude);
                }

                // 2. Fallback ke root
                if (isNaN(lat) || isNaN(lng)) {
                    lat = parseFloat(t.latitude ?? t.lat);
                    lng = parseFloat(t.longitude ?? t.lng);
                }

                // 3. Tolak jika (0,0) atau invalid
                if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
                    return false;
                }
                return true;
            });
        },
    },
    watch: {
        selectedTechnicianFromStore: {
            handler(tech) {
                this.cancelPendingCentering();

                if (!tech) {
                    this.closePanel();
                    return;
                }

                this.selectedDetail = tech;

                let lat, lng;
                if (tech.address && typeof tech.address === 'object') {
                    lat = parseFloat(tech.address.latitude);
                    lng = parseFloat(tech.address.longitude);
                }
                if (isNaN(lat) || isNaN(lng)) {
                    lat = parseFloat(tech.latitude ?? tech.lat);
                    lng = parseFloat(tech.longitude ?? tech.lng);
                }

                const hasValidCoords = !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0);

                if (hasValidCoords) {
                    this.draggablePosition = { lat, lng };
                    this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
                    this.$nextTick(() => {
                        if (this.mapObject) this.mapObject.invalidateSize();
                    });
                } else {
                    this.draggablePosition = null;
                    this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
                    this.resetMapToDefault();
                    return;
                }

                this.$nextTick(() => {
                    this.centerAndHighlight(tech);
                });
            },
            immediate: true,
            deep: true,
        },
        '$store.state.map.draggableMarkerPosition': {
            handler(newPos) {
                if (newPos) {
                    this.draggablePosition = { ...newPos };
                    const markerComp = this.$refs.draggableMarker;
                    if (markerComp && markerComp.mapObject) {
                        markerComp.mapObject.setLatLng([newPos.lat, newPos.lng]);
                    }
                } else {
                    this.draggablePosition = null;
                }
            },
            deep: true,
            immediate: true
        },
    },
    beforeDestroy() {
        if (this.highlightInterval) clearInterval(this.highlightInterval);
        this.$root.$off('map-fly-to-pin', this.flyToPin);
        // Reset state agar tidak terbuka otomatis saat kembali ke halaman
        this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);
        this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
        // Filter area tidak perlu di-reset, biarkan all sebagai default
        this.$store.commit('map/SET_AREA_FILTER', 'all');
    },
    mounted() {
        this.$root.$on('map-fly-to-pin', this.flyToPin);
    },
    methods: {
        // getEditingIcon() {
        //     return L.icon({
        //         iconUrl: 'https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
        //         shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        //         iconSize: [25, 41],
        //         iconAnchor: [12, 41],
        //         popupAnchor: [1, -34],
        //         shadowSize: [41, 41]
        //     });
        // },
        getHomePinIcon(user) {
            if (!user) return L.divIcon({ html: '' });
            const area = user.address?.area || 'West';
            // Warna latar pin
            const bgColor = area === 'West'
                ? '#3b82f6'   // merah = sibuk
                : area === 'East'
                    ? '#16a34a' // hijau = tersedia
                    : '#94a3b8'; // abu = unknown / belum install
            const initial = (user.name || '?').charAt(0).toUpperCase();

            return L.divIcon({
                html: `
                    <div class="pin-marker-container" style="position: relative; width: 30px; height: 40px; text-align: center;">
                        <svg width="30" height="40" viewBox="0 0 30 40" style="display: block;">
                            <path d="M15 0 C6.716 0 0 6.716 0 15 C0 21.5 7.5 29 15 40 C22.5 29 30 21.5 30 15 C30 6.716 23.284 0 15 0 Z" fill="${bgColor}" stroke="#ffffff" stroke-width="2"/>
                        </svg>
                        <div style="position: absolute; top: 7px; left: 0; right: 0; color: white; font-weight: bold; font-size: 13px; line-height: 1; text-shadow: 0 0 2px rgba(0,0,0,0.5);">${initial}</div>
                    </div>
                `,
                className: 'custom-home-pin',
                iconSize: [30, 40],
                iconAnchor: [15, 40], // ujung pin tepat di koordinat
                popupAnchor: [0, -40],
            });
        },
        // Helper untuk mendapatkan koordinat rumah
        getHomeLatLng(user) {
            if (!user) return [0, 0];
            const lat = parseFloat(user.address?.latitude);
            const lng = parseFloat(user.address?.longitude);
            if (isNaN(lat) || isNaN(lng)) return null;
            return [lat, lng];
        },
        resetMapToDefault() {
            if (this.isFlying || this.suppressZoomSync) return;
            this.internalUpdate = true;
            this.suppressZoomSync = true;
            const defaultCenter = [-2.5489, 118.0149];
            const defaultZoom = 5.56;
            if (this.mapObject) {
                this.mapObject.setView(defaultCenter, defaultZoom, { animate: true, duration: 0.5 });
            }
            this.$store.commit('map/SET_CENTER', { lat: defaultCenter[0], lng: defaultCenter[1] });
            this.$store.commit('map/SET_ZOOM', defaultZoom);
            this.draggablePosition = null;
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
            if (this.lastClickedMarker) {
                this.lastClickedMarker.setZIndexOffset(0);
                this.lastClickedMarker._icon?.classList.remove('marker-highlighted');
                this.lastClickedMarker = null;
            }
            this.activeHighlightId = null;
            setTimeout(() => {
                this.internalUpdate = false;
                this.suppressZoomSync = false;
                this.mapObject?.invalidateSize();
            }, 600);
        },
        onMapDblClick(e) {
            if (!this.showDraggableMarker) return;
            const { lat, lng } = e.latlng;
            this.flyToPin(lat, lng, 16);
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
        },
        onMarkerDragEnd(e) {
            const marker = e.target;
            const { lat, lng } = marker.getLatLng();
            this.draggablePosition = { lat, lng };
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
        },
        flyToPin(lat, lng, zoomLevel = 16) {
            this.draggablePosition = { lat, lng };
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            if (!this.mapObject) return;
            this.isFlying = true;
            this.internalUpdate = true;
            this.suppressZoomSync = true;
            this.mapObject.setView([lat, lng], zoomLevel, { animate: true, duration: 0.6 });
            setTimeout(() => {
                // Ambil posisi terakhir setelah animasi selesai
                const currentCenter = this.mapObject.getCenter();
                const currentZoom = this.mapObject.getZoom();
                // Commit ke store terlebih dahulu
                this.$store.commit('map/SET_CENTER', { lat: currentCenter.lat, lng: currentCenter.lng });
                this.$store.commit('map/SET_ZOOM', currentZoom);
                // Baru setelah itu lepas flag pelindung
                this.isFlying = false;
                this.internalUpdate = false;
                this.suppressZoomSync = false;
                this.mapObject.invalidateSize();
            }, 800);
        },
        onMapReady(map) {
            this.mapObject = map;
            this.$nextTick(() => {
                map.invalidateSize();
                this.startHighlightWatcher();
                if (this.selectedTechnicianFromStore) this.centerAndHighlight(this.selectedTechnicianFromStore);
            });
        },
        onMarkerClick(tech) {
            // Normalisasi data dengan format address sebagai objek (konsisten)
            const normalized = {
                id: tech.user_id,
                user_id: tech.user_id,
                name: tech.name,
                area: tech.address?.area || tech.area || 'West',
                address: {
                    address: tech.address?.address || '',
                    latitude: tech.address?.latitude ?? tech.latitude ?? null,
                    longitude: tech.address?.longitude ?? tech.longitude ?? null,
                    radius: tech.address?.radius ?? tech.radius ?? 80,
                    area: tech.address?.area || tech.area || 'West'
                }
            };
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', normalized);
            this.applyHighlight();
        },
        applyHighlight() {
            const tech = this.$store.state.map.selectedTechnician;
            if (!tech || !tech.user_id) return;

            // Coba cari marker reguler dulu
            const markerComp = this.$refs[`marker-${tech.user_id}`];
            let marker = markerComp?.[0]?.mapObject || markerComp?.mapObject;

            // Jika marker reguler tidak ada (karena difilter oleh validUsersFiltered),
            // gunakan draggable marker sebagai gantinya
            if (!marker || !marker._icon) {
                const dragComp = this.$refs.draggableMarker;
                marker = dragComp?.mapObject || null;
            }

            if (!marker || !marker._icon) return;

            // Bersihkan highlight sebelumnya
            if (this.lastClickedMarker && this.lastClickedMarker !== marker) {
                this.lastClickedMarker.setZIndexOffset(0);
                const prevIcon = this.lastClickedMarker._icon;
                if (prevIcon) {
                    prevIcon.classList.remove('marker-highlighted');
                    prevIcon.style.zIndex = '';
                    if (prevIcon.parentNode) prevIcon.parentNode.style.zIndex = '';
                }
            }

            // Naikkan z-index marker yang dipilih agar muncul paling depan
            marker.setZIndexOffset(999999);
            if (typeof marker.bringToFront === 'function') {
                marker.bringToFront();
            }
            const icon = marker._icon;
            if (icon) {
                icon.style.zIndex = '999999';
                icon.classList.add('marker-highlighted');
                if (icon.parentNode) {
                    icon.parentNode.style.zIndex = '999999';
                }
            }
            this.lastClickedMarker = marker;
            this.activeHighlightId = tech.user_id;
        },
        startHighlightWatcher() {
            if (this.highlightInterval) return;
            this.highlightInterval = setInterval(() => {
                if (this.selectedTechnicianFromStore && this.mapObject) this.applyHighlight();
            }, 500);
        },
        cancelPendingCentering() {
            if (this.moveEndHandler) {
                this.mapObject?.off('moveend', this.moveEndHandler);
                this.moveEndHandler = null;
            }
            this.isCentering = false;
            this.internalUpdate = false;
            this.isFlying = false;
            this.suppressZoomSync = false;
        },
        getLatLng(tech) {
            let lat, lng;
            if (tech.address && typeof tech.address === 'object') {
                lat = parseFloat(tech.address.latitude);
                lng = parseFloat(tech.address.longitude);
            }
            if (isNaN(lat) || isNaN(lng)) {
                lat = parseFloat(tech.latitude ?? tech.lat);
                lng = parseFloat(tech.longitude ?? tech.lng);
            }
            return [isNaN(lat) ? 0 : lat, isNaN(lng) ? 0 : lng];
        },
        async centerAndHighlight(tech) {
            if (!this.mapObject) {
                setTimeout(() => this.centerAndHighlight(tech), 200);
                return;
            }

            let lat, lng;
            if (tech.address && typeof tech.address === 'object') {
                lat = parseFloat(tech.address.latitude);
                lng = parseFloat(tech.address.longitude);
            }
            if (isNaN(lat) || isNaN(lng)) {
                lat = parseFloat(tech.latitude ?? tech.lat);
                lng = parseFloat(tech.longitude ?? tech.lng);
            }

            if (isNaN(lat) || isNaN(lng)) {
                this.resetMapToDefault();
                return;
            }

            this.isCentering = true;
            this.internalUpdate = true;
            this.suppressZoomSync = true;
            this.mapObject.invalidateSize();
            this.mapObject.setView([lat, lng], 18, { animate: true, duration: 0.5 });
            await new Promise(resolve => setTimeout(resolve, 700));
            this.$store.commit('map/CENTERING', { gmp: { latitude: lat, longitude: lng }, zoom: 18, type: 'user' });
            this.isCentering = false;
            this.internalUpdate = false;
            this.suppressZoomSync = false;
            this.mapObject.invalidateSize();
            this.$nextTick(() => this.applyHighlight());
        },
        closePanel() {
            this.draggablePosition = null;
            if (this.lastClickedMarker) {
                this.lastClickedMarker.setZIndexOffset(0);
                this.lastClickedMarker._icon?.classList.remove('marker-highlighted');
                this.lastClickedMarker = null;
            }
            this.selectedDetail = null;
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
        },
        getCustomIcon(tech) {
            const area = tech.area || 'West';
            const iconUrl = area === 'West'
                ? require('@/assets/images/caricon/marker-west.svg')
                : require('@/assets/images/caricon/marker-east.svg');
            const labelHtml = this.$store.state.users.markerPlate
                ? `<div class="marker-label bg-white rounded px-1 shadow-sm"><strong>${tech.name}</strong></div>`
                : '';
            const html = `
          <div class="marker-wrap">
            ${labelHtml}
            <div class="marker-pin marker-pin--${area === 'West' ? 'west' : 'east'}">
              <img src="${iconUrl}" />
            </div>
          </div>`;
            return L.divIcon({ html, className: 'custom-leaflet-marker', iconSize: [36, 50], iconAnchor: [18, 50] });
        },
        toggleSidebar() { this.$store.commit('sidebar/toggleSideBar'); },
        invalidateMap() {
            if (this.mapObject) {
                this.$nextTick(() => {
                    this.mapObject.invalidateSize();
                });
            }
        },
    },
};
</script>

<style scoped>
.mc-liveview__map {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.mc-map-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
}

.mc-map {
    height: 100%;
    width: 100%;
    z-index: 0;
}

.mc-liveview__map,
.mc-map-wrapper,
.mc-map {
    height: 100% !important;
    /* jika perlu, override */
    width: 100% !important;
}

.mc-map-legend {
    position: absolute;
    bottom: 24px;
    left: 16px;
    z-index: 500;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    padding: 8px 12px;
    border-radius: 8px;
    display: flex;
    gap: 14px;
    font-size: 0.75rem;
}

.legend-marker {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 4px;
}

.west-marker {
    background-color: #3b82f6;
}

.east-marker {
    background-color: #16a34a;
    /* hijau */
}

.slide-panel-enter-active,
.slide-panel-leave-active {
    transition: transform 0.3s ease;
}

.slide-panel-enter,
.slide-panel-leave-to {
    transform: translateX(100%);
}

/* global marker styles */
.custom-leaflet-marker .marker-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.custom-leaflet-marker .marker-label {
    padding: 2px 6px;
    font-size: 0.6875rem;
    background: white;
    border-radius: 4px;
    margin-bottom: 2px;
}

.custom-leaflet-marker .marker-pin {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: white;
    border: 2px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.custom-leaflet-marker .marker-pin--west {
    border-color: #3b82f6;
}

.custom-leaflet-marker .marker-pin--east {
    border-color: #16a34a;
    /* hijau */
}

.custom-leaflet-marker.marker-highlighted .marker-pin {
    transform: scale(1.2);
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.3);
}

/* Style untuk draggable marker */
.draggable-marker-pin {
    display: flex;
    justify-content: center;
    align-items: center;
    filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.3));
    transition: transform 0.1s ease;
}

.draggable-marker-pin i {
    font-size: 32px;
    color: #ef4444;
}

/* Saat marker di-drag, beri efek scale */
.leaflet-marker-dragging .draggable-marker-pin {
    transform: scale(1.2);
}

.draggable-marker-fa {
    background: transparent !important;
    border: none !important;
}

.draggable-marker-fa i {
    text-shadow: 0 2px 3px rgba(0, 0, 0, 0.3);
}

.draggable-circle-marker {
    background: transparent !important;
}

.leaflet-marker-dragging .leaflet-marker-icon {
    transition: transform 0.1s ease;
    transform: scale(1.1);
    cursor: grabbing;
}
</style>
